import { create, enforce, skipWhen, test } from "vest";

import type { ContactKind, DialstackContact } from "services/dialstack";
import { emailIsValid, maxLength, required } from "utils/validations.utils";

export type ContactPhoneRow = { number: string; label: string };

export type ContactFormValues = {
  name: string;
  organization: string;
  kind: ContactKind;
  email: string;
  notes: string;
  phone_numbers: ContactPhoneRow[];
};

export const EMPTY_PHONE_ROW: ContactPhoneRow = { number: "", label: "" };

export const EMPTY_CONTACT_FORM: ContactFormValues = {
  name: "",
  organization: "",
  kind: "other",
  email: "",
  notes: "",
  phone_numbers: [EMPTY_PHONE_ROW]
};

export function phoneDigits(value: string) {
  return value.replace(/\D/g, "").replace(/^1(?=\d{10}$)/, "");
}

export function contactToFormValues(
  contact: DialstackContact
): ContactFormValues {
  return {
    name: contact.name,
    organization: contact.organization,
    kind: contact.kind ?? "other",
    email: contact.email ?? "",
    notes: contact.notes ?? "",
    phone_numbers:
      contact.phone_numbers.length > 0
        ? contact.phone_numbers.map(({ number, label }) => ({
            number,
            label
          }))
        : [EMPTY_PHONE_ROW]
  };
}

// What the API gets: trimmed fields, bare digits, and no number twice —
// typing the same line in two rows is a slip, not two numbers.
export function formValuesToInput(values: ContactFormValues) {
  const seen = new Set<string>();
  const phone_numbers = values.phone_numbers.flatMap((row) => {
    const number = phoneDigits(row.number);
    if (!number || seen.has(number)) return [];
    seen.add(number);
    return [{ number, label: row.label.trim() }];
  });
  return {
    name: values.name.trim(),
    organization: values.organization.trim(),
    kind: values.kind,
    email: values.email.trim(),
    notes: values.notes.trim(),
    phone_numbers
  };
}

// The attach-flow subset (SaveContactDialog): the call's number is the
// contact's number, so no phone rows and no email.
export const contactValidator = ({
  withPhoneNumbers = true
}: { withPhoneNumbers?: boolean } = {}) =>
  create((data: ContactFormValues) => {
    required("name", "Name", data.name.trim());
    maxLength("name", "Name", data.name, 255);
    maxLength("organization", "Organization", data.organization, 255);
    skipWhen(!data.email.trim(), () => {
      emailIsValid("email", "Email", data.email.trim());
    });
    skipWhen(!withPhoneNumbers, () => {
      test("phone_numbers", "Add at least one phone number", () => {
        enforce(
          data.phone_numbers.filter((row) => phoneDigits(row.number)).length
        ).greaterThan(0);
      });
      data.phone_numbers.forEach((row, index) => {
        test(
          `phone_numbers.${index}.number`,
          "Enter a 10-digit phone number",
          () => {
            enforce(phoneDigits(row.number).length).equals(10);
          }
        );
      });
    });
  });
